import {useState, useCallback, useEffect, useMemo} from 'react';
import produce from 'immer';
import {useSMCSnackbar} from '../../../utils/hooks';
import {useTranslation} from 'react-i18next';
import {setProperty, getErrorCode} from '../../../utils/utils';
import Api from '../../../api/SMPFetchHelper';
import {ObjectTypes, ApiErrorCodes, ErrorCodes} from '../../../api/consts';
import {useSMCErrorHandler} from '../../../utils/errorUtils';
import {ServiceDomainGetSchema} from '../../../api/DBSchema/ServiceDomain';
import generalIcon from '../../../assets/images/general.png';
import pageIcon from '../../../assets/images/page.png';
import historyIcon from '../../../assets/images/history.png';
import ServiceDomainGeneral from './InnerPages/ServiceDomainGeneral';
import ServiceDomainNotifications from './InnerPages/ServiceDomainNotifications';
import ServiceDomainHistory from './InnerPages/ServiceDomainHistory';
import {helpContextPageUrl} from '../../../conf/Routes';

export function useServiceDomainEditor(serviceDomainId, defaultChosenPageId) {
    const { t } = useTranslation();
    const { enqueueSuccessSnackbar, enqueueErrorSnackbar, enqueueLogoutSnackbar } = useSMCSnackbar();
    const { getErrorMessage, getMessagesArrayByPages } = useSMCErrorHandler();

    const [origServiceDomainObject, setOrigServiceDomainObject] = useState({});
    const [serviceDomainObject, setServiceDomainObject] = useState({});

    const [isPageDataLoaded, setIsPageDataLoaded] = useState(false);
    const [isInnerPageLoading, setIsInnerPageLoading] = useState(false);
    const [isPageLoadFailed, setIsPageLoadFailed] = useState(false);
    const [errorMessagesByPageList, setErrorMessagesByPageList] = useState(null);

    const [isObjectDirty, setIsObjectDirty] = useState(false);

    const [chosenPageId, setChosenPageId] = useState(defaultChosenPageId || 'general');

    const [secondsBetweenFetchDomainRequests] = useState(3);

    const editorPages = useMemo(() => ([
        { id: ServiceDomainGetSchema.pages.general.pageId, title: t('SMP.GENERAL'), icon: generalIcon, pageElement: ServiceDomainGeneral, helpId: helpContextPageUrl.serverDomainGeneral },        
        { id: ServiceDomainGetSchema.pages.notifications.pageId, title: t('SMP.SERVICE_DOMAIN.SETTINGS.INNER_MENU.NOTIFICATIONS'), icon: pageIcon, pageElement: ServiceDomainNotifications, helpId: helpContextPageUrl.domainNotifications },
        { id: ServiceDomainGetSchema.pages.history.pageId, title: t('SMP.HISTORY'), icon: historyIcon, pageElement: ServiceDomainHistory, helpId: helpContextPageUrl.serverDomainHistory }
    ]), [t]);

    const [pageIdToPageName, pageIdToHelpId] = useMemo(() => {
        const _pageIdToPageName = {};
        const _pageIdToHelpId = {};
        const addPagesData = (pagesRoot) => {
            pagesRoot.forEach(page => {
                if (page.id) {
                    _pageIdToPageName[page.id] = page.title;
                    _pageIdToHelpId[page.id] = page.helpId;
                }
                if (page.children) {
                    addPagesData(page.children);
                }
            })
        }
        addPagesData(editorPages);
        return [_pageIdToPageName, _pageIdToHelpId];
    }, [editorPages]);

    const helpId = useMemo(() => pageIdToHelpId[chosenPageId], [pageIdToHelpId, chosenPageId]);

    const fetchServiceDomain = useCallback(async () => {
        setIsPageDataLoaded(false);
        let timer = 0;
        let intervalID = setInterval(async () => {
            timer += secondsBetweenFetchDomainRequests;
        }, secondsBetweenFetchDomainRequests * 1000);
        let errorCode;
        while (timer < 50) {
            try {
                const fetchServiceDomainResp = await Api.fetchSingleObject(ObjectTypes.SERVICE_DOMAIN, serviceDomainId);
                const serviceDomain = fetchServiceDomainResp.data.data.serviceDomain;
                // Validate server response
                if (!serviceDomain) {
                    console.error('Get service domain response failed: service domain response returned empty');
                    throw new Error(ErrorCodes.SERVER_ERROR);
                }
                setOrigServiceDomainObject(serviceDomain);
                setServiceDomainObject(serviceDomain);
                setIsPageLoadFailed(false);
                setIsObjectDirty(false);
                errorCode = '';
                break;
            } catch (e) {
                errorCode = getErrorCode(e) || ApiErrorCodes.GENERAL_ERROR;
                if (errorCode === ApiErrorCodes.INVALID_SESSION_ERROR) {
                    enqueueLogoutSnackbar(t(errorCode));
                    break;
                } else if (errorCode !== ApiErrorCodes.OBJ_NOT_EXISTS) {
                    errorCode = ApiErrorCodes.GENERAL_ERROR;
                    break;
                }
            }

            await new Promise(r => setTimeout(r, secondsBetweenFetchDomainRequests * 1000));
        }

        if(errorCode=== ApiErrorCodes.OBJ_NOT_EXISTS){
            errorCode=ApiErrorCodes.GENERAL_ERROR;
        }
        if (errorCode !== '') {
            if(errorCode !== ApiErrorCodes.INVALID_SESSION_ERROR){
                enqueueErrorSnackbar(t(errorCode));
            }
            setIsPageLoadFailed(true);
        }

        clearInterval(intervalID);
        setIsPageDataLoaded(true);
    }, [serviceDomainId, enqueueErrorSnackbar, enqueueLogoutSnackbar, t, secondsBetweenFetchDomainRequests]);
    
    const setServiceDomainData = useCallback((paramPath, newValue) => {
        setIsObjectDirty(true); // Data changed, object is dirty
        setServiceDomainObject(serviceDomainObj => produce(serviceDomainObj, draft => {
            setProperty(draft, paramPath, newValue);
        }));
    }, []);

    const verifyData = useCallback(() => {
        setIsInnerPageLoading(true);
        Api.verifySingleObject(ObjectTypes.SERVICE_DOMAIN, serviceDomainId, serviceDomainObject)
        .then(() => {
            // On no error, clear error message
            setErrorMessagesByPageList(null);
        })
        .catch((error) => {
            const errorMessageArrayByPage = getMessagesArrayByPages(error, pageIdToPageName);
            if (errorMessageArrayByPage) {
                setErrorMessagesByPageList(errorMessageArrayByPage);
            }
        })
        .finally(() => {
            setIsInnerPageLoading(false);
        });
    }, [serviceDomainId, getMessagesArrayByPages, serviceDomainObject, pageIdToPageName]);

    const onPageChange = useCallback((pageId) => {
        verifyData();
        setChosenPageId(pageId);
    }, [verifyData]);


    const saveButtonOnClick = useCallback(() => {
        Api.updateSingleObject(ObjectTypes.SERVICE_DOMAIN, serviceDomainId, serviceDomainObject)
        .then(() => {
            setIsPageDataLoaded(false);
            setErrorMessagesByPageList(null);
            fetchServiceDomain();
            enqueueSuccessSnackbar(t('OBJECT_EDITOR_OBJ_SAVED_OK'));
        })
        .catch((error) => {
            const errorMessage = getErrorMessage(error);
            alert(errorMessage);
        })
        .finally(() => {
            setIsPageDataLoaded(true);
        });
    }, [fetchServiceDomain, getErrorMessage, serviceDomainId, serviceDomainObject, enqueueSuccessSnackbar, t]);

    const revertButtonOnClick = useCallback(() => {
        setServiceDomainObject(origServiceDomainObject);
        setIsObjectDirty(false); // Is not dirty, object is reset to originl values
        setErrorMessagesByPageList(null);
    }, [origServiceDomainObject]);

    // Fetch service domain on loading of page
    useEffect(() => {
        fetchServiceDomain();
    }, [fetchServiceDomain]);
    
    return {
        serviceDomainObject,
        isObjectDirty,
        editorPages,
        helpId,
        isPageDataLoaded,
        isInnerPageLoading,
        isPageLoadFailed,
        chosenPageId,
        errorMessagesByPageList,
        onPageChange,
        setServiceDomainData,
        saveButtonOnClick,
        revertButtonOnClick,
        reloadPageData: fetchServiceDomain
    };
}